import {
  CheckImageSmallDetailsCommand,
  SmallDetailsPayload,
} from '@/typings/SmallDetails';
import { WizardPayload } from '@/web-components/logo-library/typings';
import imageProcessingAPI from '@/services/FileService';
import { mm2px, px2mm } from '@/utils';
import { lineWidth } from '@/constants/areaLineWidth';
import { Polygon, Vector } from 'collider2d';
import { isConvex } from '@/utils/collisionHelper';
import { getSmallDetailsStatus } from '../components/SmallDetails/index';
import { base64StringToBlob } from 'blob-util';
import {
  SmallDetailsPrintPosition,
  ProductPrintAreaPoint,
  ProductPrintArea,
} from '@midocean/webapps-shared-domain';

export default {
  async analyzeSmallDetails(
    positions: SmallDetailsPrintPosition[]
  ): Promise<SmallDetailsPayload[]> {
    if (positions.length > 0) {
      const results = await Promise.all(
        positions.map(async (position) => {
          const command: CheckImageSmallDetailsCommand = {
            id: position.artworkId,
            logoWidthInMm: position.calculatedWidth,
            positiveLimitInMm: position.positiveLimitMM,
            negativeLimitInMm: position.negativeLimitMM,
            ignoreColors: position.ignoreColors,
          };
          const { data } = await imageProcessingAPI.checkImageSmallDetails(
            command,
            position.file
          );

          return {
            positiveDetailsLoss: data.positiveDetailsLoss,
            negativeDetailsLoss: data.negativeDetailsLoss,
            calculatedQuality: data.calculatedQuality,
            pngFile: data.pngFile !== null ? data.pngFile : '',
            pngFileBlob:
              data.pngFile !== null
                ? base64StringToBlob(data.pngFile, 'image/png')
                : new Blob(),
            id: data.id,
            isCheckValid: true,
            approvedAt: undefined,
            status: getSmallDetailsStatus(
              data.positiveDetailsLoss,
              data.negativeDetailsLoss,
              data.calculatedQuality
            ),
          } as SmallDetailsPayload;
        })
      );

      results.sort((a, b) => a.calculatedQuality - b.calculatedQuality);

      return results;
    }

    return [];
  },

  calculateArtworkSize(
    payload: WizardPayload,
    activePosition: SmallDetailsPrintPosition
  ) {
    const artworkSpaceConsumed = 97; // value in percents
    let calcArtworkWidth = 0;
    let calcArtworkHeight = 0;
    const CORRECTION_FACTOR = 0;

    const { originalImageWebDpi, originalImageWidthPx, originalImageDpi } =
      activePosition;

    const webImageWidthPx =
      originalImageWidthPx &&
      originalImageWebDpi &&
      originalImageDpi &&
      (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;
    const rectLineWidth = (lineWidth * (webImageWidthPx as number)) / 100;

    const aspectRatioWH = payload.pngWidthPixels / payload.pngHeightPixels;
    const aspectRatioHW = payload.pngHeightPixels / payload.pngWidthPixels;

    if (activePosition && activePosition.printArea?.shapeType === 'Rectangle') {
      const areaRect = {
        x: this.drawPoints(activePosition).x + rectLineWidth / 2,
        y: this.drawPoints(activePosition).y + rectLineWidth / 2,
        w: this.drawPoints(activePosition).w - rectLineWidth,
        h: this.drawPoints(activePosition).h - rectLineWidth,
      };

      const artworkRatio = payload.pngWidthPixels / payload.pngHeightPixels;
      const areaRatio = areaRect.w / areaRect.h;

      // Artwork is more landscape than area - fit to width
      if (artworkRatio > areaRatio) {
        calcArtworkWidth = areaRect.w - CORRECTION_FACTOR;
        calcArtworkHeight =
          (calcArtworkWidth / payload.pngWidthPixels) * payload.pngHeightPixels;
      }
      // Artwork is more portrait than area - fit to height
      else {
        calcArtworkHeight = areaRect.h - CORRECTION_FACTOR;
        calcArtworkWidth =
          (calcArtworkHeight / payload.pngHeightPixels) *
          payload.pngWidthPixels;
      }
    }

    if (activePosition && activePosition.printArea?.shapeType === 'Ellipse') {
      const min =
        this.drawPoints(activePosition).rx < this.drawPoints(activePosition).ry
          ? this.drawPoints(activePosition).rx
          : this.drawPoints(activePosition).ry;
      let size = 0;
      if (
        Math.abs(
          this.drawPoints(activePosition).ry -
            this.drawPoints(activePosition).rx
        ) > 630
      ) {
        size = min * 2;
      } else {
        size = min * 1.6;
      }

      if (aspectRatioWH >= aspectRatioHW) {
        calcArtworkWidth = (size * (artworkSpaceConsumed - 15)) / 100;
        calcArtworkHeight = calcArtworkWidth / aspectRatioWH;
      } else {
        calcArtworkHeight = (size * (artworkSpaceConsumed - 15)) / 100;
        calcArtworkWidth = calcArtworkHeight / aspectRatioHW;
      }
    }

    if (activePosition && activePosition.printArea?.shapeType === 'Polygon') {
      const size = this.polygonDetails(activePosition).minDistance;

      if (aspectRatioWH >= aspectRatioHW) {
        calcArtworkWidth = (size * artworkSpaceConsumed) / 100;
        calcArtworkHeight = calcArtworkWidth / aspectRatioWH;
      } else {
        calcArtworkHeight = (size * artworkSpaceConsumed) / 100;
        calcArtworkWidth = calcArtworkHeight / aspectRatioHW;
      }
    }

    return px2mm(calcArtworkWidth, originalImageWebDpi as number);
  },

  polygonDetails(activePosition: SmallDetailsPrintPosition) {
    const vectors = [];
    for (let i = 0; i < this.area(activePosition).length; i++) {
      vectors.push(
        new Vector(
          this.area(activePosition)[i]?.x,
          this.area(activePosition)[i]?.y
        )
      );
    }
    const polygon = new Polygon(new Vector(0, 0), vectors);
    const centroid = polygon.getCentroid();

    let min = Number.MAX_VALUE;

    if (!isConvex(polygon)) {
      const points = [...polygon.points];
      for (let i = 0; i < points.length; i++) {
        const point = points[i].clone();
        const distanceToPoint = point.sub(centroid).len();
        if (distanceToPoint < min) {
          min = distanceToPoint;
        }
      }
    } else {
      const points = [...polygon.points];
      for (let i = 0; i < points.length; i++) {
        let next = i + 1;
        if (next >= points.length) {
          next = 0;
        }

        // calculate slope ---> (y2-y1)/(x2-x1)
        const k =
          (points[next].y - points[i].y) / (points[next].x - points[i].x);

        // calcualte n --->  y = kx + n; n = y - kx;
        const n = points[i].y - k * points[i].x;

        // transform to Ax + By + C = 0 --> - kx + y - n = 0;
        const a = -k;
        const b = 1;
        const c = -n;

        // calculate distance ---> d = abs(a*x0 + b*y0 + c) / sqrt(a*a + b*b)
        const distanceToSide = Math.abs(
          (a * centroid.x + b * centroid.y + c) / Math.sqrt(a * a + b * b)
        );
        if (distanceToSide < min) {
          min = distanceToSide;
        }
      }
    }
    return {
      x: centroid.x,
      y: centroid.y,
      minDistance: min * 1.5,
    };
  },

  area(activePosition: SmallDetailsPrintPosition | undefined) {
    if (!activePosition) return [];

    const { printArea, originalImageWebDpi } = activePosition;
    const points: ProductPrintAreaPoint[] = (printArea as ProductPrintArea)
      ?.productPrintAreaPoints;

    if (!points) return [];

    return [...points]
      .sort((a, b) => (a.sequenceNo > b.sequenceNo ? 1 : -1))
      .map((point: ProductPrintAreaPoint) => {
        return {
          x: mm2px(
            point['distanceFromLeftEdge'],
            originalImageWebDpi === 0 ? 96 : originalImageWebDpi
          ),
          y: mm2px(
            point['distanceFromTop'],
            originalImageWebDpi === 0 ? 96 : originalImageWebDpi
          ),
        };
      });
  },

  drawPoints(activePosition: SmallDetailsPrintPosition): any | undefined {
    if (!this.area(activePosition).length) return;

    const {
      printArea,
      originalImageWebDpi,
      originalImageWidthPx,
      originalImageDpi,
    } = activePosition;

    if (printArea?.shapeType === 'Rectangle') {
      const webImageWidthPx =
        (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;
      const rectLineWidth = (lineWidth * webImageWidthPx) / 100;

      return {
        x: this.area(activePosition)[0]?.x - rectLineWidth / 2,
        y: this.area(activePosition)[0]?.y - rectLineWidth / 2,
        w: mm2px(printArea.widthMm, originalImageWebDpi) + rectLineWidth,
        h: mm2px(printArea.heightMm, originalImageWebDpi) + rectLineWidth,
      };
    }
    if (printArea?.shapeType === 'Ellipse') {
      return {
        x:
          (this.area(activePosition)[0].x + this.area(activePosition)[1].x) / 2,
        y:
          (this.area(activePosition)[0].y + this.area(activePosition)[1].y) / 2,
        rx:
          (this.area(activePosition)[1].x - this.area(activePosition)[0].x) / 2,
        ry:
          (this.area(activePosition)[1].y - this.area(activePosition)[0].y) / 2,
      };
    }
    if (printArea?.shapeType === 'Polygon') {
      return [...this.area(activePosition), this.area(activePosition)[0]];
    }
  },
};
